import React, { useMemo, useState, useEffect, useContext } from 'react';
import classnames from 'classnames';
import { useRequest } from 'ahooks';
import TipContext from '@home/components/TipContext';
import { context as Context } from '@gp/components/Context';
import Loading from '@gp/components/Loading';
import CardSlot from '@gp/components/CardSlot';
import CollapsiblePanelList from '@gp/pages/StudioDashboard/StudioOverall/Tabs/Management/Details/components/CollapsiblePanelList';
import StudioManagementMeetingHeader from '@gp/pages/StudioDashboard/StudioOverall/Tabs/Management/Details/components/Meetings/components/MeetingHeader';
import StudioManagementMeetingContent from '@gp/pages/StudioDashboard/StudioOverall/Tabs/Management/Details/components/Meetings/components/MeetingContent';
import BIDSNavigator from '@gp/pages/StudioDashboard/StudioOverall/Tabs/Management/Details/components/BIDSNavigator';
import usePageLog from '@gp/hooks/usePageLog';
import useBIDSLinker from '@gp/pages/StudioDashboard/StudioOverall/Tabs/Management/Details/components/Meetings/hooks/useBIDSLinker';
import useMeetingPanelInitialConfig from '@gp/pages/StudioDashboard/StudioOverall/Tabs/Management/Details/components/Meetings/hooks/useMeetingPanelInitialConfig';
import useAttachmentPreview from '@gp/pages/StudioDashboard/StudioOverall/Tabs/Management/Details/components/Meetings/hooks/useAttachmentPreview';
import useAttachmentDownload from '@gp/pages/StudioDashboard/StudioOverall/Tabs/Management/Details/components/Meetings/hooks/useAttachmentDownload';
import { queryMeetingsOfManagement } from '@gp/pages/StudioDashboard/StudioOverall/services/studioOverallDetail';
import { getLocationIsValid } from '@gp/utils/common';
import { getValidMeetings } from '@gp/pages/StudioDashboard/StudioOverall/Tabs/Management/Details/components/Meetings/utils';

import './index.scss';

/**
 * 构建会议折叠面板Element
 * @param meeting 会议
 * @param expandedMeetingId 展开的会议id
 * @param attachmentDownloadings 附件下载loading状态集合
 * @param onAttachmentPreview 文档预览的处理方法
 * @param onAttachmentDownload 文档下载的处理方法
 * @returns 会议折叠面板Element
 */
function buildMeetingPanel(
  meeting: IStudioManagementMeeting,
  expandedMeetingId: string | number,
  attachmentDownloadings: IStudioManagementMeetingAttachmentDownloadings,
  onAttachmentPreview: IStudioManagementMeetingAttachmentActionHandler,
  onAttachmentDownload: IStudioManagementMeetingAttachmentActionHandler,
): IStudioManagementCollapsiblePanel {
  // 不是默认展开的会议记录，都默认折叠
  const isCollapsed = String(expandedMeetingId) !== String(meeting?.id);
  return {
    isCollapsed,
    data: meeting,
    headerRender: (isCollapsed) => <StudioManagementMeetingHeader isCollapsed={isCollapsed} meeting={meeting} />,
    bodyRender: (isCollapsed) => (
      <StudioManagementMeetingContent
        isCollapsed={isCollapsed}
        meeting={meeting}
        attachmentDownloadings={attachmentDownloadings}
        onAttachmentPreview={onAttachmentPreview}
        onAttachmentDownload={onAttachmentDownload}
      />
    ),
  };
}

const StudioManagementMeetings: React.FC<IStudioManagementMeetingsProps> = (props) => {
  const { style, className, maxShow: maxShowProps } = props;

  const filterObj: Record<string, any> = getLocationIsValid('/Studio/PortfolioOverview');

  const {
    studioId,
    studioName,
    // 默认展开的会议id
    meetingId: defaultExpandedMeetingId,
    // 默认展开的会议id setter
    setMeetingId,
  } = useContext<StudioOverallDetailContextTypes>(Context);

  const {
    loading: isMeetingsLoading,
    data: meetings,
    run: runQueryMeetingsOfManagement,
  } = useRequest(queryMeetingsOfManagement, {
    manual: true,
  });

  // 当前展开的会议的id
  const [expandedMeetingId, setExpandedMeetingId] = useState(defaultExpandedMeetingId || filterObj?.meetingId || '');

  // 过滤无效会议数据，面板header组件 需要展示，会议名称、会议决议时间、会议创建信息（创建时间、创建人）
  const validMeetings = useMemo(() => getValidMeetings(meetings), [meetings]);

  const isHasData = useMemo(
    () => !isMeetingsLoading && validMeetings && validMeetings?.length > 0,
    [isMeetingsLoading, validMeetings],
  );
  const isNoData = useMemo(
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    () => !isMeetingsLoading && (!validMeetings || !validMeetings?.length),
    [isMeetingsLoading, validMeetings],
  );

  // 跳转BISD系统相关的
  const BIDSSystemURL = useBIDSLinker(studioId);

  // 文档预览相关
  const { onAttachmentPreview } = useAttachmentPreview(studioId);

  // 文档下载
  const {
    // 文档下载loading状态集合
    attachmentDownloadings,
    // 文档下载处理函数
    onAttachmentDownload,
  } = useAttachmentDownload({
    studioId,
    meetings: validMeetings,
    setExpandedMeetingId,
  });

  // 会议记录列表折叠面板列表数据
  const meetingsPanels = useMemo<IStudioManagementCollapsiblePanel[]>(() => {
    // 组装CollapsiblePanelList会议折叠面板组件数据
    const panels: IStudioManagementCollapsiblePanel[] = validMeetings?.map((meeting) =>
      buildMeetingPanel(meeting, expandedMeetingId, attachmentDownloadings, onAttachmentPreview, onAttachmentDownload),
    );
    return panels || [];
  }, [validMeetings, expandedMeetingId, attachmentDownloadings, onAttachmentPreview, onAttachmentDownload]);

  // 会议折叠面板初始化配置相关的
  const { maxShow, isShowAll } = useMeetingPanelInitialConfig({
    expandedMeetingId,
    meetingsPanels,
    maxShow: maxShowProps,
  });

  usePageLog({
    pageId: 'STUDIO_OVERALL_DETAILS_MANAGEMENT_BOARD_GOVERNANCE',
    uidType: 'studio_id',
    uid: studioId || '',
    extInfo: studioName,
  });

  useEffect(() => {
    if (!studioId) return;
    runQueryMeetingsOfManagement({ studio_id: studioId });
  }, [studioId]);

  useEffect(
    () =>
      // 离开会议记录页签时，清空默认展开会议记录，下次再进入会议记录页签时，默认全部折叠
      () => {
        setMeetingId('');
      },
    [],
  );

  return (
    <div style={style} className={classnames('dbg-studio-management-meetings-card', className)}>
      <div className="dbg-studio-management-meetings-card-title">
        <span className="dbg-studio-management-meetings-card-title-text">Board/General Meeting & Resolution</span>
        <TipContext
          overlayClassName="dbg-studio-management-meetings-card-bubble"
          tipText={<BIDSNavigator url={BIDSSystemURL} />}
        />
      </div>

      {isMeetingsLoading || isNoData ? (
        <CardSlot>
          {/* loading动画 */}
          {isMeetingsLoading && <Loading loading style={{ height: 350 }} />}
          {/* 无数据 */}
          {isNoData && (
            <div className="dbg-studio-management-meetings-no-data">
              <BIDSNavigator url={BIDSSystemURL} />
            </div>
          )}
        </CardSlot>
      ) : null}
      <div>
        {/* 董事会议列表 */}
        {isHasData && <CollapsiblePanelList maxShow={maxShow} isShowAll={isShowAll} list={meetingsPanels} />}
      </div>
    </div>
  );
};

export default React.memo(StudioManagementMeetings);
